import { apiClient, ApiResponse } from './apiClient';
import {
  ProductMedia,
  PaginatedMediaResponse,
  UploadMediaData,
  UpdateMediaData,
  MediaParams,
  BulkDeleteData,
} from '../types/product-media.types';

/**
 * Product Media Library Service
 * Handles media management for product images
 */

export const productMediaService = {
  /**
   * Upload images to media library
   * POST /product-media/upload (multipart/form-data)
   */
  upload: async (data: UploadMediaData): Promise<ApiResponse<{ items: ProductMedia[] }>> => {
    const formData = new FormData();
    
    data.images.forEach(image => {
      formData.append('images', image);
    });
    
    if (data.tags && data.tags.length > 0) {
      // Backend expects tags[] array format
      data.tags.forEach(tag => {
        formData.append('tags[]', tag);
      });
    }

    return await apiClient.uploadFile<{ items: ProductMedia[] }>('/product-media/upload', formData);
  },

  /**
   * Get media library (paginated, searchable, filterable)
   * GET /product-media
   */
  getMedia: async (params?: MediaParams): Promise<ApiResponse<PaginatedMediaResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.tags && params.tags.length > 0) {
      // Backend expects tags[] array format
      params.tags.forEach(tag => {
        queryParams.append('tags[]', tag);
      });
    }
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.order) queryParams.append('order', params.order);

    const url = `/product-media${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiClient.get<PaginatedMediaResponse>(url);
  },

  /**
   * Get single media item by ID
   * GET /product-media/:id
   */
  getById: async (id: string): Promise<ApiResponse<{ item: ProductMedia }>> => {
    return await apiClient.get<{ item: ProductMedia }>(`/product-media/${id}`);
  },

  /**
   * Update media tags/metadata
   * PATCH /product-media/:id
   */
  update: async (
    id: string,
    data: UpdateMediaData
  ): Promise<ApiResponse<{ item: ProductMedia }>> => {
    return await apiClient.patch<{ item: ProductMedia }>(`/product-media/${id}`, data);
  },

  /**
   * Delete single media item
   * DELETE /product-media/:id
   */
  remove: async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
    return await apiClient.delete<{ success: boolean }>(`/product-media/${id}`);
  },

  /**
   * Bulk delete media items
   * POST /product-media/bulk-delete
   */
  bulkDelete: async (data: BulkDeleteData): Promise<ApiResponse<null>> => {
    return await apiClient.post<null>('/product-media/bulk-delete', data);
  },
};
